import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { logger } from '@/utils/logger';
import { WIPDepartment, IWIPTask, WIP_DEPARTMENT_LABELS } from '@/types/psi';
import WIPKanbanBoard from './WIPKanbanBoard';

// Mock data for demonstration
const MOCK_WIP_TASKS: IWIPTask[] = [
  // Purchase Department
  {
    id: 'p1',
    title: 'Connector quotation from Molex',
    department: 'purchase',
    column: 'rfq',
    priority: 'high',
    reference: 'RFQ-2024-001',
    assignee: 'Alice',
    createdAt: '2024-12-20T10:00:00Z',
    updatedAt: '2024-12-20T10:00:00Z',
  },
  {
    id: 'p2',
    title: 'PCB order for Project Alpha',
    department: 'purchase',
    column: 'po',
    priority: 'medium',
    reference: 'PO-2024-042',
    amount: 125000,
    assignee: 'Bob',
    dueDate: '2024-12-28',
    createdAt: '2024-12-18T09:00:00Z',
    updatedAt: '2024-12-22T14:30:00Z',
  },
  {
    id: 'p3',
    title: 'Capacitor stock replenishment',
    department: 'purchase',
    column: 'restock',
    priority: 'low',
    reference: 'RS-2024-015',
    createdAt: '2024-12-19T11:00:00Z',
    updatedAt: '2024-12-19T11:00:00Z',
  },
  {
    id: 'p4',
    title: 'Invoice #INV-2024-089 payment',
    department: 'purchase',
    column: 'payable',
    priority: 'urgent',
    reference: 'INV-2024-089',
    amount: 85000,
    dueDate: '2024-12-25',
    createdAt: '2024-12-15T08:00:00Z',
    updatedAt: '2024-12-20T16:00:00Z',
  },

  // Sales Department
  {
    id: 's1',
    title: 'Customer A - IoT Module Quote',
    department: 'sales',
    column: 'quotation',
    priority: 'high',
    reference: 'QT-2024-015',
    amount: 320000,
    assignee: 'Carol',
    createdAt: '2024-12-21T09:00:00Z',
    updatedAt: '2024-12-21T09:00:00Z',
  },
  {
    id: 's2',
    title: 'Customer B - Sensor Order',
    department: 'sales',
    column: 'so',
    priority: 'medium',
    reference: 'SO-2024-089',
    amount: 156000,
    assignee: 'David',
    createdAt: '2024-12-19T14:00:00Z',
    updatedAt: '2024-12-22T10:00:00Z',
  },
  {
    id: 's3',
    title: 'Order #1234 - Ready to ship',
    department: 'sales',
    column: 'shipment',
    priority: 'urgent',
    reference: 'SO-2024-078',
    dueDate: '2024-12-24',
    createdAt: '2024-12-18T11:00:00Z',
    updatedAt: '2024-12-23T09:00:00Z',
  },
  {
    id: 's4',
    title: 'Delivery to Customer C',
    department: 'sales',
    column: 'dispatch',
    priority: 'medium',
    reference: 'DL-2024-045',
    createdAt: '2024-12-22T15:00:00Z',
    updatedAt: '2024-12-23T08:00:00Z',
  },
  {
    id: 's5',
    title: 'Invoice #2024-156 collection',
    department: 'sales',
    column: 'receivable',
    priority: 'high',
    reference: 'INV-2024-156',
    amount: 245000,
    dueDate: '2024-12-30',
    createdAt: '2024-12-10T10:00:00Z',
    updatedAt: '2024-12-20T11:00:00Z',
  },

  // Finance Department
  {
    id: 'f1',
    title: 'Vendor ABC payment',
    department: 'finance',
    column: 'payable',
    priority: 'medium',
    reference: 'PAY-2024-033',
    amount: 50000,
    dueDate: '2024-12-27',
    createdAt: '2024-12-20T10:00:00Z',
    updatedAt: '2024-12-20T10:00:00Z',
  },
  {
    id: 'f2',
    title: 'Customer D overdue invoice',
    department: 'finance',
    column: 'receivable',
    priority: 'urgent',
    reference: 'RCV-2024-089',
    amount: 180000,
    dueDate: '2024-12-15',
    createdAt: '2024-12-01T09:00:00Z',
    updatedAt: '2024-12-22T14:00:00Z',
  },
  {
    id: 'f3',
    title: 'December payroll processing',
    department: 'finance',
    column: 'salary',
    priority: 'high',
    amount: 850000,
    dueDate: '2024-12-25',
    assignee: 'Eva',
    createdAt: '2024-12-20T08:00:00Z',
    updatedAt: '2024-12-23T10:00:00Z',
  },
  {
    id: 'f4',
    title: 'Weekly cash reconciliation',
    department: 'finance',
    column: 'cash_ledger',
    priority: 'low',
    assignee: 'Frank',
    createdAt: '2024-12-23T09:00:00Z',
    updatedAt: '2024-12-23T09:00:00Z',
  },

  // Manufacturing Department
  {
    id: 'm1',
    title: 'Batch #456 - PCB Assembly',
    department: 'manufacturing',
    column: 'mo',
    priority: 'medium',
    reference: 'MO-2024-033',
    assignee: 'George',
    createdAt: '2024-12-21T08:00:00Z',
    updatedAt: '2024-12-21T08:00:00Z',
  },
  {
    id: 'm2',
    title: 'Line 2 - Next week schedule',
    department: 'manufacturing',
    column: 'scheduling',
    priority: 'low',
    assignee: 'Helen',
    createdAt: '2024-12-22T10:00:00Z',
    updatedAt: '2024-12-22T10:00:00Z',
  },
  {
    id: 'm3',
    title: 'Defective units from Batch #440',
    department: 'manufacturing',
    column: 'return_mfg_good',
    priority: 'high',
    reference: 'RTN-2024-012',
    description: '15 units with soldering defects',
    createdAt: '2024-12-19T14:00:00Z',
    updatedAt: '2024-12-22T11:00:00Z',
  },
  {
    id: 'm4',
    title: 'Capacitor return to vendor',
    department: 'manufacturing',
    column: 'return_material',
    priority: 'medium',
    reference: 'MRT-2024-008',
    description: 'Wrong specification received',
    createdAt: '2024-12-20T09:00:00Z',
    updatedAt: '2024-12-21T15:00:00Z',
  },
  {
    id: 'm5',
    title: 'Resistor refill for Line 1',
    department: 'manufacturing',
    column: 'refill_material',
    priority: 'urgent',
    reference: 'REF-2024-022',
    createdAt: '2024-12-23T07:00:00Z',
    updatedAt: '2024-12-23T07:00:00Z',
  },
];

const DEPARTMENTS: WIPDepartment[] = ['purchase', 'sales', 'finance', 'manufacturing'];

const WIPContainer: React.FC = () => {
  const { t, i18n } = useTranslation('psi');
  const [selectedDepartment, setSelectedDepartment] = useState<WIPDepartment>('purchase');
  const [selectedTask, setSelectedTask] = useState<IWIPTask | null>(null);

  const language = i18n.language.startsWith('zh') ? 'zh' : 'en';

  // Filter tasks by selected department
  const filteredTasks = MOCK_WIP_TASKS.filter(
    (task) => task.department === selectedDepartment
  );

  const handleTaskClick = (task: IWIPTask) => {
    setSelectedTask(task);
    // TODO: Open task detail modal
    logger.log('Task clicked:', task);
  };

  const getDepartmentLabel = (dept: WIPDepartment) => {
    return language === 'zh'
      ? WIP_DEPARTMENT_LABELS[dept].zh
      : WIP_DEPARTMENT_LABELS[dept].en;
  };

  // Department color themes
  const departmentColors: Record<WIPDepartment, string> = {
    purchase: 'bg-blue-500',
    sales: 'bg-green-500',
    finance: 'bg-purple-500',
    manufacturing: 'bg-orange-500',
  };

  return (
    <div className="space-y-4">
      {/* Header with Department Selector */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 ${departmentColors[selectedDepartment]} rounded-lg`}>
            <svg
              className="w-5 h-5 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"
              />
            </svg>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('wip.title', 'Work-In-Progress')}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('wip.subtitle', 'Track tasks across departments')}
            </p>
          </div>
        </div>

        {/* Department Dropdown */}
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600 dark:text-gray-400">
            {t('wip.selectDepartment', 'Department')}:
          </label>
          <select
            value={selectedDepartment}
            onChange={(e) => setSelectedDepartment(e.target.value as WIPDepartment)}
            className="block w-48 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            {DEPARTMENTS.map((dept) => (
              <option key={dept} value={dept}>
                {getDepartmentLabel(dept)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Department Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {DEPARTMENTS.map((dept) => {
          const count = MOCK_WIP_TASKS.filter((t) => t.department === dept).length;
          const isActive = dept === selectedDepartment;
          return (
            <button
              key={dept}
              onClick={() => setSelectedDepartment(dept)}
              className={`p-3 rounded-lg border transition-all ${
                isActive
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className={`text-sm font-medium ${
                  isActive ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-600 dark:text-gray-400'
                }`}>
                  {getDepartmentLabel(dept)}
                </span>
                <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${
                  isActive
                    ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-800 dark:text-indigo-300'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                }`}>
                  {count}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Kanban Board */}
      <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4">
        <WIPKanbanBoard
          department={selectedDepartment}
          tasks={filteredTasks}
          onTaskClick={handleTaskClick}
          language={language}
        />
      </div>

      {/* Task Detail Modal Placeholder */}
      {selectedTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {selectedTask.title}
              </h3>
              <button
                onClick={() => setSelectedTask(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-3 text-sm">
              {selectedTask.reference && (
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Reference: </span>
                  <span className="text-gray-900 dark:text-white font-mono">{selectedTask.reference}</span>
                </div>
              )}
              {selectedTask.description && (
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Description: </span>
                  <span className="text-gray-900 dark:text-white">{selectedTask.description}</span>
                </div>
              )}
              {selectedTask.assignee && (
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Assignee: </span>
                  <span className="text-gray-900 dark:text-white">{selectedTask.assignee}</span>
                </div>
              )}
              {selectedTask.amount !== undefined && (
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Amount: </span>
                  <span className="text-green-600 dark:text-green-400 font-medium">
                    NT$ {selectedTask.amount.toLocaleString()}
                  </span>
                </div>
              )}
              {selectedTask.dueDate && (
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Due Date: </span>
                  <span className="text-gray-900 dark:text-white">{selectedTask.dueDate}</span>
                </div>
              )}
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setSelectedTask(null)}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WIPContainer;
